import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import type { CoreUIIconSize } from '../core-ui-icon/core-ui-icon.component';

export interface CoreUIContextMenuItem {
  id: string;
  label: string;
  icon: string;
  iconSize?: CoreUIIconSize;
  disabled?: boolean;
  destructive?: boolean;
  separatorBefore?: boolean;
}

export type CoreUIContextMenuSize = 'sm' | 'md';
export type CoreUIContextMenuAppearance = 'light' | 'dark';
export type CoreUIContextMenuVariant = 'default' | 'file-actions';

@Component({
  selector: 'core-ui-context-menu',
  templateUrl: './core-ui-context-menu.component.html',
  styleUrls: ['./core-ui-context-menu.component.scss'],
  standalone: false,
})
export class CoreUIContextMenuComponent implements AfterViewInit {
  @Input() items: readonly CoreUIContextMenuItem[] = [];
  @Input() ariaLabel = 'Acciones';
  @Input() size: CoreUIContextMenuSize = 'md';
  @Input() appearance: CoreUIContextMenuAppearance = 'light';
  @Input() variant: CoreUIContextMenuVariant = 'default';
  @Input() openDirection: 'above' | 'below' = 'below';
  @Output() itemSelected = new EventEmitter<string>();
  @Output() dismissed = new EventEmitter<void>();
  @ViewChild('menuSurface') private menuSurface?: ElementRef<HTMLElement>;
  @ViewChildren('menuItem') private menuItems?: QueryList<ElementRef<HTMLButtonElement>>;

  ngAfterViewInit(): void {
    queueMicrotask(() => this.menuSurface?.nativeElement.focus());
  }

  onItemClick(item: CoreUIContextMenuItem): void {
    if (!item.disabled) this.itemSelected.emit(item.id);
  }

  trackByItemId(_: number, item: CoreUIContextMenuItem): string {
    return item.id;
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const key = event.key;
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.dismissed.emit();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return;

    event.preventDefault();
    const buttons = this.enabledButtons();
    if (!buttons.length) return;
    const current = buttons.indexOf(this.menuItems?.get(index)?.nativeElement as HTMLButtonElement);
    const target = key === 'Home'
      ? 0
      : key === 'End'
        ? buttons.length - 1
        : (current + (key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[target].focus();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.dismissed.emit();
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const buttons = this.enabledButtons();
    const target = event.key === 'ArrowUp' || event.key === 'End' ? buttons.length - 1 : 0;
    buttons[target]?.focus();
  }

  private enabledButtons(): HTMLButtonElement[] {
    return (this.menuItems?.toArray() ?? [])
      .map((item) => item.nativeElement)
      .filter((button) => !button.disabled);
  }
}
